import {
  Button,
  Flex,
  Icon,
  Icons,
  Paragraph,
  Table,
  useToast,
  type TableColumnType,
  type TableColumnVerticalAlign,
} from "@ir/ui";
import type { PurchaserBrandAssociation } from "@src/domain/entities/purchaser";
import { BrandAssociationStatusToggle } from "./BrandAssociationStatusToggle";

const verticalAlign: TableColumnVerticalAlign = "middle";

interface AssociatedBrandRow {
  key: string;
  brandName: string;
  purchaserInboxEmailId: string | null;
  association: PurchaserBrandAssociation;
}

interface AssociatedBrandsTableProps {
  purchaserId: number;
  brands: PurchaserBrandAssociation[];
  onAssociationUpdated: (association: PurchaserBrandAssociation) => void;
}

function buildColumns(
  purchaserId: number,
  onAssociationUpdated: (association: PurchaserBrandAssociation) => void,
  onCopyInboxEmail: (email: string) => void,
): TableColumnType<AssociatedBrandRow>[] {
  return [
    {
      title: "Brand Name",
      dataIndex: "brandName",
      key: "brandName",
      align: "left",
      verticalAlign,
      cellRender: (value: unknown) => (
        <Paragraph className="m-0">{value as string}</Paragraph>
      ),
    },
    {
      title: "System Inbox Email",
      dataIndex: "purchaserInboxEmailId",
      key: "purchaserInboxEmailId",
      align: "left",
      verticalAlign,
      cellRender: (value: unknown) => {
        const email = value as string | null;
        return (
          <Flex align="center" gap={0.5}>
            <Paragraph className="m-0 text-grey-500">{email ?? "—"}</Paragraph>
            {email ? (
              <Button
                size="small"
                variant="clear"
                color="grey"
                onPress={() => onCopyInboxEmail(email)}
              >
                <Icon name={Icons.Copy} />
              </Button>
            ) : null}
          </Flex>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "association",
      key: "status",
      align: "center",
      verticalAlign,
      width: 80,
      cellRender: (_value: unknown, record: AssociatedBrandRow) => (
        <Flex align="center" justifyContent="center">
          <BrandAssociationStatusToggle
            purchaserId={purchaserId}
            association={record.association}
            onAssociationUpdated={onAssociationUpdated}
          />
        </Flex>
      ),
    },
  ];
}

export const AssociatedBrandsTable = ({
  purchaserId,
  brands,
  onAssociationUpdated,
}: AssociatedBrandsTableProps) => {
  const { success: successToast } = useToast();

  const dataSource: AssociatedBrandRow[] = brands.map((association) => ({
    key: String(association.brandPurchaserId),
    brandName: association.brandName,
    purchaserInboxEmailId: association.purchaserInboxEmailId,
    association,
  }));

  const columns = buildColumns(purchaserId, onAssociationUpdated, (email) => {
    navigator.clipboard.writeText(email);
    successToast({
      content: "System Inbox Email copied to clipboard",
      icon: <Icon name={Icons.CheckCircle} />,
    });
  });

  return (
    <Table<AssociatedBrandRow>
      columns={columns}
      dataSource={dataSource}
      rowHoverable
      aria-label="Associated brands table"
      scroll={{ x: "100%", y: 300 }}
    />
  );
};
